import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import { themr } from 'react-css-themr';
import PropTypes from 'prop-types';
import { AUTOCOMPLETE } from 'react-toolbox/lib/identifiers';
import { compose } from 'recompose';
import { translate } from 'react-i18next';
import { Input as InjectInput, InputTypes } from 'common/components/core/input';
import events from 'react-toolbox/lib/utils/events';
import theme from './Autocomplete.scss';
import { Portal } from 'react-portal';
import MlFontIcon from 'common/components/core/font_icon/MlFontIcon';

import SelectableItem from './SelectableItem';

const POSITION = {
    AUTO: 'auto',
    DOWN: 'down',
    UP: 'up'
};

const factory = ({ Input, SelectableItem, defaultTheme }) => {
    class AutocompleteOverflow extends Component {
        static propTypes = {
            className: PropTypes.string,
            direction: PropTypes.oneOf(['auto', 'up', 'down']),
            disabled: PropTypes.bool,
            error: PropTypes.string,
            label: PropTypes.string,
            multiple: PropTypes.bool,
            onChange: PropTypes.func,
            selectedPosition: PropTypes.oneOf(['above', 'below']),
            showSuggestionsWhenValueIsSet: PropTypes.bool,
            source: PropTypes.any,
            suggestionMatch: PropTypes.oneOf(['start', 'anywhere', 'word']),
            minimized: PropTypes.bool,
            showOverlay: PropTypes.bool,
            theme: PropTypes.shape({
                active: PropTypes.string,
                autocomplete: PropTypes.string,
                focus: PropTypes.string,
                input: PropTypes.string,
                label: PropTypes.string,
                suggestion: PropTypes.string,
                suggestions: PropTypes.string,
                up: PropTypes.string,
                value: PropTypes.string,
                values: PropTypes.string
            }),
            value: PropTypes.any
        };

        static defaultProps = {
            className: '',
            direction: 'auto',
            selectedPosition: 'above',
            multiple: true,
            showSuggestionsWhenValueIsSet: false,
            source: {},
            suggestionMatch: 'start',
            showOverlay: false,
            emptyText: 'noResultsFound',
            emptyImage: ''
        };

        state = {
            direction: this.props.direction,
            focus: false,
            showAllSuggestions: this.props.showSuggestionsWhenValueIsSet,
            query: this.query(this.props.value)
        };

        UNSAFE_componentWillMount() {
            document.body.addEventListener(
                'mouseup',
                this.handleMouseUp.bind(this)
            );
        }

        UNSAFE_componentWillReceiveProps(nextProps) {
            if (!this.props.multiple) {
                this.setState({
                    query: this.query(nextProps.value)
                });
            }
        }

        shouldComponentUpdate(nextProps) {
            if (
                !this.props.focus &&
                nextProps.focus &&
                this.props.direction === POSITION.AUTO
            ) {
                const direction = this.calculateDirection();
                if (this.state.direction !== direction) {
                    this.setState({
                        direction
                    });
                    return false;
                }
            }
            return true;
        }

        handleChange = (keys, item, event, keepFocus) => {
            const key = this.props.multiple ? keys : keys[0];
            const query = this.query(key);
            if (this.props.onChange) this.props.onChange(key, item, event);
            this.setState({
                focus: keepFocus,
                query,
                showAllSuggestions: this.props.showSuggestionsWhenValueIsSet
            });
        };

        handleQueryBlur = () => {
            if (this.props.focus)
                this.setState({
                    focus: false
                });
        };

        handleQueryChange = value => {
            this.setState({
                query: value,
                showAllSuggestions: false
            });
        };

        handleQueryFocus = () => {
            if (this.refs.suggestions) {
                this.refs.suggestions.scrollTop = 0;
            }
            this.setState({
                active: '',
                focus: true
            });
        };

        handleQueryKeyDown = event => {
            // Clear query when pressing backspace and showing all suggestions.
            const shouldClearQuery =
                event.which === 8 &&
                this.props.showSuggestionsWhenValueIsSet &&
                this.state.showAllSuggestions;
            if (shouldClearQuery) {
                this.setState({
                    query: ''
                });
            }

            if (event.which == 27) {
                if (this.props.focus)
                    this.setState({
                        focus: false
                    });
            }
        };

        handleClickOutside = event => {
            this.props.handleClickOutside(event);
        };

        handleQueryKeyUp = event => {
            if (event.which === 13) {
                let target = this.state.active;
                if (!target) {
                    target = [...this.suggestions().keys()][0];
                    this.setState({
                        active: target
                    });
                }
                var item = this.refs[target];
                if (item && !item.props.isSelected) {
                    this.select(target, item, event);
                }
            }

            if (event.which === 27)
                ReactDOM.findDOMNode(this).querySelector('input').blur();

            if ([40, 38].indexOf(event.which) !== -1) {
                const suggestionsKeys = [...this.suggestions().keys()];
                let index =
                    suggestionsKeys.indexOf(this.state.active) +
                    (event.which === 40 ? +1 : -1);
                if (index < 0) index = suggestionsKeys.length - 1;
                if (index >= suggestionsKeys.length) index = 0;
                this.setState({
                    active: suggestionsKeys[index]
                });
            }
        };

        handleSuggestionHover = key => {
            this.setState({
                active: key
            });
        };
        handleSuggestionHoverOut = () => {
            this.setState({
                active: false
            });
        };

        calculateDirection() {
            if (this.props.direction === 'auto') {
                const client = ReactDOM.findDOMNode(
                    this.refs.input
                ).getBoundingClientRect();
                const screen_height =
                    window.innerHeight || document.documentElement.offsetHeight;
                const up = client.top > screen_height / 2 + client.height;
                return up ? 'up' : 'down';
            } else {
                return this.props.direction;
            }
        }

        query(key) {
            return !this.props.multiple && key
                ? this.source().get(key)
                : this.state
                ? this.state.query
                : '';
        }

        suggestions() {
            let suggest = new Map();
            const query = this.state.query.toLowerCase().trim() || '';
            const source = this.source();

            // Suggest any non-set value which matches the query
            if (this.props.multiple) {
                for (const [key, value] of source) {
                    if (this.matches(value.toLowerCase().trim(), query)) {
                        suggest.set(key, value);
                    }
                }
                // When multiple is false, suggest any value which matches the query if showAllSuggestions is false
            } else if (query && !this.state.showAllSuggestions) {
                for (const [key, value] of source) {
                    if (this.matches(value.toLowerCase().trim(), query)) {
                        suggest.set(key, value);
                    }
                }
                // When multiple is false, suggest all values when showAllSuggestions is true
            } else {
                suggest = source;
            }

            return suggest;
        }

        matches(value, query) {
            if (query && this.props.notSearchAbleValues.indexOf(value) !== -1) {
                return false;
            }
            const { suggestionMatch } = this.props;

            if (suggestionMatch === 'start') {
                return value.startsWith(query);
            } else if (suggestionMatch === 'anywhere') {
                return value.includes(query);
            } else if (suggestionMatch === 'word') {
                const re = new RegExp(`\\b${query}`, 'g');
                return re.test(value);
            }

            return false;
        }

        source() {
            const { source: src } = this.props;
            if (src.hasOwnProperty('length')) {
                return new Map(
                    src.map(item =>
                        Array.isArray(item) ? [...item] : [item, item]
                    )
                );
            } else {
                return new Map(Object.keys(src).map(key => [key, src[key]]));
            }
        }

        values() {
            const valueMap = new Map();
            const vals = this.props.multiple
                ? this.props.value
                : [this.props.value];
            const source = this.source();
            vals.forEach(val => {
                if (source.has(val)) {
                    valueMap.set(val, source.get(val));
                }
            });
            return valueMap;
        }

        select(key, item, event) {
            events.pauseEvent(event);
            const values = this.values(this.props.value);
            this.handleChange([key, ...values.keys()], item, event, false);
        }

        unselect(key, item, event) {
            event.stopPropagation();
            if (!this.props.disabled) {
                const values = this.values(this.props.value);
                values.delete(key);
                this.handleChange([...values.keys()], item, event, true);
            }
        }

        handleSelectClick() {
            if (!this.props.focus)
                this.setState({
                    focus: true
                });
        }
        renderSuggestions() {
            const { theme, emptyText, emptyImage, t } = this.props;
            const values = this.values();
            const suggestions = [...this.suggestions()].map(([key, value]) => {
                const className = classnames(theme.suggestion, {
                    [theme.active]: this.state.active === key
                });
                return (
                    <SelectableItem
                        key={key}
                        theme={theme}
                        className={className}
                        ref={key}
                        onSelect={this.select.bind(this, key)}
                        onUnselect={this.unselect.bind(this, key)}
                        onMouseOver={this.handleSuggestionHover.bind(this, key)}
                        onMouseOut={this.handleSuggestionHoverOut.bind(
                            this,
                            key
                        )}
                        isSelected={values.has(key)}
                        id={key}
                        value={value}
                    >
                        {value}
                    </SelectableItem>
                );
            });

            const className = classnames(theme.suggestions, {
                [theme.up]: this.state.direction === 'up'
            });
            return suggestions.length > 0 ? (
                <ul ref="suggestions" className={className}>
                    {suggestions}
                </ul>
            ) : (
                <div className={classnames(className, theme.empty)}>
                    <span>{t(emptyText)}</span>
                    {emptyImage ? <img src={emptyImage} /> : null}
                </div>
            );
        }

        handleMouseUp = event => {
            var selectionsRef = this.refs.autocomplete;
            if (selectionsRef) {
                var element = ReactDOM.findDOMNode(selectionsRef);
                if (element) {
                    if (
                        !element.isEqualNode(event.target) ||
                        !element.contains(event.target)
                    ) {
                        if (this.props.focus)
                            this.setState({
                                focus: false
                            });
                    }
                }
            }
        };

        render() {
            const {
                error,
                label,
                theme,
                placeholder,
                showOverlay,
                ...other
            } = this.props;
            const className = classnames(
                theme.autocomplete,
                {
                    [theme.focus]: this.props.focus,
                    [theme.minimized]: this.props.minimized
                },
                this.props.className
            );
            const searchIcon = <MlFontIcon iconClass="search" />;
            return (
                <div className={theme.autocompleteWrapper}>
                    <div
                        ref="autocomplete"
                        data-react-toolbox="autocomplete"
                        onMouseUp={this.handleMouseUp}
                        className={className}
                    >
                        <Input
                            {...other}
                            inputType={InputTypes.NoBorder}
                            theme={theme}
                            autoFocus
                            ref="input"
                            className={theme.input}
                            error={error}
                            label={label}
                            onChange={this.handleQueryChange}
                            onFocus={this.handleQueryFocus}
                            onKeyDown={this.handleQueryKeyDown}
                            onKeyUp={this.handleQueryKeyUp}
                            value={this.state.query}
                            floating={false}
                            icon={searchIcon}
                            placeholder={placeholder}
                        />
                        {this.renderSuggestions()}
                    </div>
                    {showOverlay ? (
                        <Portal>
                            <div className={theme.overlay} />
                        </Portal>
                    ) : null}
                </div>
            );
        }
    }

    return compose(
        themr(AUTOCOMPLETE, defaultTheme),
        translate('translations')
    )(AutocompleteOverflow);
};

const factoryWithCustomSelectableItem = CustomSelectableItem =>
    factory({
        Input: InjectInput,
        SelectableItem: CustomSelectableItem,
        defaultTheme: theme
    });
const AutocompleteOverflow = factory({
    Input: InjectInput,
    SelectableItem,
    defaultTheme: theme
});
export default AutocompleteOverflow;
export {
    factory as autocompleteOverflowFactory,
    factoryWithCustomSelectableItem,
    AutocompleteOverflow
};
